import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useRef, useState } from 'react';
import { z } from 'zod';

import { resultCount } from '@/lib/result-count';

import { MIN_SEARCH_TERM_LENGTH, useDebouncedCallback } from '@/lib/useDebouncedCallback';

import { DataTable, type Column, type SortState } from '@/components/data/DataTable';
import { PageHeader } from '@/components/layout/PageHeader';
import { RequireCapability } from '@/components/layout/RequireCapability';
import { EntityChip } from '@/components/primitives/EntityChip';
import { LiveDataIndicator } from '@/components/primitives/LiveDataIndicator';
import { StateChip } from '@/components/primitives/StateChip';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import {
  PLACEHOLDER_EXPLANATION,
  useSquadList,
  type SquadListRow,
} from '@/features/squads/squads';

const searchSchema = z.object({
  q: z.string().optional(),
  lifecycle: z.enum(['active', 'archived', 'any']).default('active'),
  includePlaceholders: z.boolean().optional(),
  sort: z.string().default('name'),
  direction: z.enum(['asc', 'desc']).default('asc'),
  page: z.coerce.number().default(1),
});

export const Route = createFileRoute('/squads/')({
  validateSearch: searchSchema,
  component: SquadsScreen,
});

function SquadsScreen() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  /**
   * A term below the API's minimum is refused there rather than scanned for, so the
   * request is not sent at all. Derived from the URL (the committed value), not the
   * text box, so it matches what would actually go over the wire.
   */
  const committedTooShort =
    (search.q?.trim().length ?? 0) > 0 &&
    (search.q?.trim().length ?? 0) < MIN_SEARCH_TERM_LENGTH;

  const { data, isPending, isFetching, isError, error, refetch, dataUpdatedAt } = useSquadList(
    {
      q: search.q,
      lifecycle: search.lifecycle,
      includePlaceholders: search.includePlaceholders,
      sort: search.sort,
      direction: search.direction,
      page: search.page,
    },
    !committedTooShort,
  );

  /*
    The text box is local state; the URL is written on a debounce.

    This search is a collection scan on a ZazzBackend-owned collection — the same
    database the live consumer app reads from — so one request per keystroke is real
    load on production, not just a wasted round trip. The URL stays the source of
    truth so a pasted link and a reload are unchanged.
  */
  const [term, setTerm] = useState(search.q ?? '');
  // The last value this input itself produced. Comparing the URL against it is what
  // separates "the debounce just landed" from "the URL changed underneath us".
  const sentRef = useRef(search.q ?? '');

  // Adjust during render rather than in an effect: an effect would fire a second
  // render pass, and syncing state from props in one is the cascade React warns
  // about. The guard means this only runs on a genuine external change —
  // back/forward, a pasted link, or the Clear button — never on our own commit.
  const urlTerm = search.q ?? '';
  if (urlTerm !== sentRef.current) {
    sentRef.current = urlTerm;
    setTerm(urlTerm);
  }

  const commitTerm = useDebouncedCallback((value: string) => {
    setSearch({ q: value || undefined });
  }, 300);

  const termTooShort = term.trim().length > 0 && term.trim().length < MIN_SEARCH_TERM_LENGTH;

  function setSearch(next: Record<string, unknown>) {
    void navigate({
      search: (previous) => ({ ...previous, ...next, page: (next.page as number) ?? 1 }),
      replace: true,
    });
  }

  const columns: Column<SquadListRow>[] = [
    {
      id: 'name',
      header: 'Squad',
      sortable: true,
      width: 'minmax(200px, 1.8fr)',
      mobile: 'primary',
      cell: (squad) => (
        <div className="min-w-0">
          <EntityChip
            kind="squad"
            id={squad.id}
            name={squad.name}
            secondary={squad.tagline ?? undefined}
            imageUrl={squad.imageUrl ?? undefined}
            to={`/squads/${squad.id}`}
          />
          {squad.lifecycleStatus === 'archived' && (
            <div className="mt-1">
              <StateChip state="archived" size="sm" />
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'members',
      header: 'Members',
      width: '96px',
      align: 'right',
      mobile: 'secondary',
      cell: (squad) => <span className="mesh-mono text-tx2">{squad.memberCount}</span>,
    },
    {
      id: 'entries',
      header: 'In leagues',
      width: '104px',
      align: 'right',
      mobile: 'secondary',
      cell: (squad) => (
        <span
          className="mesh-mono"
          style={{ color: squad.activeEntries > 0 ? 'var(--tx2)' : 'var(--tx3)' }}
        >
          {squad.activeEntries}
        </span>
      ),
    },
    {
      id: 'sports',
      header: 'Sports',
      width: 'minmax(0, 1fr)',
      mobile: 'detail',
      cell: (squad) => (
        <span className="text-tx3 truncate text-[12px] capitalize">
          {squad.sportEligibility.join(', ') || '—'}
        </span>
      ),
    },
    {
      id: 'owner',
      header: 'Owner',
      width: 'minmax(0, 1fr)',
      mobile: 'detail',
      cell: (squad) => (
        <span className="text-tx2 truncate text-[12.5px]">{squad.owner?.name ?? '—'}</span>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      width: '110px',
      sortable: true,
      align: 'right',
      mobile: 'detail',
      cell: (squad) => (
        <span className="mesh-mono text-tx3">
          {squad.createdAt ? squad.createdAt.slice(0, 10) : '—'}
        </span>
      ),
    },
  ];

  const sort: SortState = { columnId: search.sort, direction: search.direction };

  return (
    <RequireCapability capability="squads.view">
      <div className="flex flex-col gap-3.5">
        <PageHeader
          eyebrow="Core squads"
          title={
            data
              ? resultCount(data.total, data.hasMore, data.rows.length, 'squad', 'squads')
              : 'Squads'
          }
          meta="Persistent groups that enter leagues together — not a league team"
          actions={
            <LiveDataIndicator
              updatedAt={dataUpdatedAt || undefined}
              isFetching={isFetching}
              onRefresh={() => void refetch()}
            />
          }
        />

        <div className="border-bd bg-card flex flex-col gap-2.5 rounded-xl border p-3">
          <div className="flex items-center gap-2">
            <div className="relative min-w-0 flex-1">
              <Search
                size={14}
                aria-hidden
                className="text-tx3 pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2"
              />
              <input
                type="search"
                value={term}
                onChange={(event) => {
                  setTerm(event.target.value);
                  sentRef.current = event.target.value;
                  commitTerm(event.target.value);
                }}
                placeholder="Search squads by name…"
                aria-label="Search squads"
                className="border-bd bg-bg-alt text-tx placeholder:text-tx3 focus-visible:border-bd-s h-11 w-full rounded-lg border pr-2.5 pl-8 text-[13px] outline-none"
              />
            </div>
            <label className="flex items-center gap-1.5">
              <span className="sr-only">Lifecycle</span>
              <select
                value={search.lifecycle}
                onChange={(event) => setSearch({ lifecycle: event.target.value })}
                aria-label="Lifecycle"
                className="border-bd bg-bg-alt text-tx2 h-11 rounded-lg border px-2 text-[12.5px] outline-none"
              >
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="any">All</option>
              </select>
            </label>
            {/*
              An escape hatch, not a filter anyone is expected to reach for: support
              occasionally needs to confirm a specific placeholder still exists.
            */}
            <button
              type="button"
              onClick={() =>
                setSearch({
                  includePlaceholders: search.includePlaceholders ? undefined : true,
                })
              }
              aria-pressed={search.includePlaceholders === true}
              title={PLACEHOLDER_EXPLANATION}
              className="font-head flex h-11 flex-none items-center rounded-lg border px-2.5 text-[11.5px] font-semibold uppercase"
              style={{
                letterSpacing: '0.12em',
                borderColor: search.includePlaceholders ? 'var(--bd-s)' : 'var(--bd)',
                background: search.includePlaceholders ? 'var(--elev)' : 'transparent',
                color: search.includePlaceholders ? 'var(--tx2)' : 'var(--tx3)',
              }}
            >
              Show placeholders
            </button>
          </div>

          {/* The API refuses a term this short rather than scanning; say so before it does. */}
          {termTooShort && (
            <p className="text-tx3 text-[11.5px]">
              Keep typing — searches run from {MIN_SEARCH_TERM_LENGTH} characters.
            </p>
          )}

          {/*
            The naming collision is ZazzBackend's and causes real support confusion,
            so it is stated on the screen rather than left to be learned the hard way.
          */}
          <p className="text-tx3 text-[11.5px]">
            A core squad is the persistent group that enters leagues together. The per-league,
            per-season team is also called a squad upstream and is a different record — those
            appear on a league's Members tab.
          </p>

          {/*
            Said on the screen rather than left to be noticed: a hidden default filter
            that silently drops most of a collection is how an operator concludes a
            squad was deleted.
          */}
          {!search.includePlaceholders && (
            <p className="text-tx3 text-[11.5px]">{PLACEHOLDER_EXPLANATION}</p>
          )}
        </div>

        {committedTooShort ? null : isError ? (
          <ErrorState error={error} subject="squads" onRetry={() => void refetch()} />
        ) : (
          <>
            <DataTable
              label="Squads"
              columns={columns}
              rows={data?.rows ?? []}
              rowKey={(squad) => squad.id}
              isLoading={isPending}
              sort={sort}
              onSortChange={(next) =>
                setSearch({ sort: next.columnId, direction: next.direction })
              }
              emptyState={
                <EmptyState
                  title="No squads match"
                  description={
                    search.lifecycle === 'active'
                      ? 'Archived squads are hidden — switch the filter to All before concluding a squad is gone.'
                      : 'Nothing matched that search.'
                  }
                />
              }
            />

            {data && data.total !== null && data.total > data.pageSize && (
              <Pagination
                page={data.page}
                pageSize={data.pageSize}
                total={data.total}
                onPage={(page) => setSearch({ page })}
              />
            )}
          </>
        )}
      </div>
    </RequireCapability>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="mesh-mono text-tx3 text-[11.5px]">
        {((page - 1) * pageSize + 1).toLocaleString()}–
        {Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="border-bd bg-elev text-tx2 hover:text-tx min-h-11 rounded-lg border px-3 text-[13px] disabled:opacity-40"
        >
          Prev
        </button>
        <span className="mesh-mono text-tx3 text-[11.5px]">
          {page} / {lastPage}
        </span>
        <button
          type="button"
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
          className="border-bd bg-elev text-tx2 hover:text-tx min-h-11 rounded-lg border px-3 text-[13px] disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
